import { Button, Dropdown, Option, Text, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    flexWrap: 'wrap',
  },
  left: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  summary: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  pageSizeLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  pageSizeDropdown: {
    minWidth: '72px',
  },
  controls: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
});

export interface TablePaginationFooterProps {
  /** Total number of items across all pages. */
  totalItems: number;
  /** Current page (1-indexed). */
  currentPage?: number;
  /** Items per page. Defaults to `totalItems` (single page). */
  pageSize?: number;
  /** Fired when the user requests a different page. Omit to render disabled controls. */
  onPageChange?: (nextPage: number) => void;
  /** Fired when the user picks a new page size. Omit to hide the size selector. */
  onPageSizeChange?: (nextSize: number) => void;
  /** Options shown in the page-size dropdown. */
  pageSizeOptions?: readonly number[];
}

/**
 * "Showing X–Y of N items · Display count: [25 ▾] · Page A of B  ◀ ▶" footer.
 *
 * - Omit `pageSize` to render as a single-page summary (no real pagination).
 * - Provide `onPageSizeChange` to render the page-size dropdown.
 * - Wrap the table in `<DataTablePanel>` and pass this as the `footer` slot
 *   to pin it to the bottom of the panel via flex layout.
 */
export const TablePaginationFooter = ({
  totalItems,
  currentPage = 1,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: TablePaginationFooterProps) => {
  const styles = useStyles();
  const effectivePageSize = pageSize ?? Math.max(totalItems, 1);
  const totalPages = Math.max(1, Math.ceil(totalItems / effectivePageSize));
  const start = totalItems === 0 ? 0 : (currentPage - 1) * effectivePageSize + 1;
  const end = Math.min(currentPage * effectivePageSize, totalItems);

  const canPrev = !!onPageChange && currentPage > 1;
  const canNext = !!onPageChange && currentPage < totalPages;

  const showSizeSelector = !!onPageSizeChange && pageSize !== undefined;

  return (
    <div className={styles.root}>
      <Text className={styles.summary}>
        Showing {start}–{end} of {totalItems} items — Page {currentPage} of {totalPages}
      </Text>
      {showSizeSelector && (
        <>
          <Text className={styles.pageSizeLabel}>·</Text>
          <span className={styles.left}>
            <Text className={styles.pageSizeLabel}>Display count:</Text>
          <Dropdown
            size="small"
            className={styles.pageSizeDropdown}
            value={String(effectivePageSize)}
            selectedOptions={[String(effectivePageSize)]}
            onOptionSelect={(_e, data) => {
              const next = Number(data.optionValue);
              if (Number.isFinite(next)) onPageSizeChange?.(next);
            }}
            aria-label="Items per page"
          >
            {pageSizeOptions.map((opt) => (
              <Option key={opt} value={String(opt)}>{String(opt)}</Option>
            ))}
          </Dropdown>
        </span>
        </>
      )}
      <span className={styles.controls}>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronLeftRegular />}
          disabled={!canPrev}
          onClick={() => onPageChange?.(currentPage - 1)}
          aria-label="Previous page"
        />
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronRightRegular />}
          disabled={!canNext}
          onClick={() => onPageChange?.(currentPage + 1)}
          aria-label="Next page"
        />
      </span>
    </div>
  );
};
